import React from "react";
import { FaShoppingBag } from "react-icons/fa";
import fallbackImg from "../../assets/logo/appstore.png";

export default function OrderDetailsDrawer({ order, onClose }) {
  if (!order) return null;

  const getStatusStyle = (status) => {
    switch (status) {
      case "Delivered":
        return "bg-green-100 text-green-600";
      case "Scheduled":
        return "bg-yellow-100 text-yellow-600";
      case "Cancelled":
        return "bg-red-100 text-red-600";
      default:
        return "bg-blue-100 text-blue-600";
    }
  };

  return (
    <>
      {/* OVERLAY */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1100] flex items-center justify-center"
        onClick={onClose}
      >
        {/* MODAL */}
        <div
          onClick={(e) => e.stopPropagation()}
          className="w-[95%] max-w-lg max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-[fadeIn_0.3s_ease]"
        >
          {/* HEADER */}
          <div className="sticky top-0 bg-gradient-to-r from-red-600 via-rose-600 to-red-500 text-white p-4.5 px-5 flex justify-between items-center shadow-md">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <FaShoppingBag className="text-white text-base" />
              </div>
              <div>
                <h2 className="font-extrabold text-base leading-tight">
                  Booking Details
                </h2>
                <p className="text-[11px] text-red-100 font-medium">
                  {order?.orderNumber || `ORD-#${order?.orderId}`}
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition cursor-pointer"
            >
              ✕
            </button>
          </div>

          {/* BODY */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4 font-sans custom-scrollbar">

            {/* STATUS & DATE BADGE */}
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 p-3 rounded-xl">
              <div>
                <span className="text-[10px] font-extrabold uppercase text-slate-400 block tracking-wider">Booking Status</span>
                <span className={`inline-block px-2.5 py-0.5 mt-0.5 text-xs rounded-md font-extrabold ${getStatusStyle(order?.status)}`}>
                  ● {order?.status || "Active / Confirmed"}
                </span>
              </div>
              <div className="text-right">
                <span className="text-[10px] font-extrabold uppercase text-slate-400 block tracking-wider">Date & Time</span>
                <span className="text-xs font-bold text-slate-700">
                  {order?.orderDate ? new Date(order.orderDate).toLocaleDateString("en-IN", { day: 'numeric', month: 'short', year: 'numeric' }) : "-"}
                </span>
              </div>
            </div>

            {/* LAB & PATIENT INFO */}
            {(order?.labName || order?.addressTitle || order?.locality) && (
              <div className="bg-slate-50 border border-slate-200 p-3.5 rounded-xl text-xs space-y-1.5">
                <h4 className="font-extrabold text-slate-900 uppercase tracking-wider text-[10px]">
                  Sample Collection Details
                </h4>
                {order?.labName && (
                  <p className="text-slate-700 font-semibold flex items-center gap-1.5">
                    🏥 Lab: <span className="text-slate-900 font-bold">{order.labName}</span>
                  </p>
                )}
                {(order?.flatHouseNumber || order?.street || order?.locality || order?.addressTitle) && (
                  <p className="text-slate-600 font-medium">
                    📍 Address: {[order.addressTitle, order.flatHouseNumber, order.street, order.locality, order.pincode].filter(Boolean).join(", ")}
                  </p>
                )}
              </div>
            )}

            {/* ITEMS */}
            <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-2xs space-y-3">
              <h3 className="font-extrabold text-xs uppercase tracking-wider text-slate-900 border-b border-slate-100 pb-2">
                Booked Tests & Packages ({order?.items?.length || order?.totalQuantity || 0})
              </h3>

              {order?.items?.length > 0 ? (
                order.items.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 pb-3 border-b border-slate-100 last:border-none last:pb-0"
                  >
                    <div className="w-11 h-11 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center text-red-600 font-extrabold flex-shrink-0 text-xs">
                      🧪
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-extrabold text-xs sm:text-sm text-slate-900 truncate">
                        {item?.itemName || `Test #${item?.ProductId || item?.productId || i + 1}`}
                      </p>
                      <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                        Quantity: {item?.Quantity || item?.quantity || 1}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="font-black text-xs sm:text-sm text-slate-900">
                        ₹{item?.TotalPrice || item?.UnitPrice || item?.unitPrice || 0}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-xs text-slate-400 py-2">
                  Health Test Booking Confirmed
                </p>
              )}
            </div>

            {/* PRICE BREAKDOWN */}
            <div className="bg-slate-50 border border-slate-200 p-3.5 rounded-xl space-y-2 text-xs">
              <h3 className="font-extrabold text-[10px] uppercase tracking-wider text-slate-900 border-b border-slate-200 pb-1.5">
                Payment Summary
              </h3>

              <div className="flex justify-between text-slate-600 font-medium">
                <span>Subtotal MRP:</span>
                <span className="font-bold text-slate-800">₹{(order?.subTotal || order?.finalAmount || 0).toFixed(2)}</span>
              </div>

              {Number(order?.discountAmount || 0) > 0 && (
                <div className="flex justify-between text-emerald-700 font-bold">
                  <span>Discount Applied:</span>
                  <span>- ₹{Number(order.discountAmount).toFixed(2)}</span>
                </div>
              )}

              <div className="flex justify-between text-slate-600 font-medium">
                <span>Home Sample Collection:</span>
                <span className="text-emerald-700 font-bold bg-emerald-100 px-1.5 py-0.5 rounded text-[10px]">FREE</span>
              </div>

              <div className="flex justify-between font-black text-sm text-slate-900 pt-2 border-t border-dashed border-slate-300">
                <span>Total Amount Paid:</span>
                <span className="text-red-600 font-black">
                  ₹{(order?.finalAmount || 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ANIMATION */}
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
    </>
  );
}